import { IconButton } from '@synoptic/ui-kit/button/icon-button.js';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from '@synoptic/ui-kit/dropdown-menu/dropdown-menu.js';
import { MoreHorizIcon } from '@synoptic/ui-kit/icons/react/more-horiz.js';
import { ModalRoot, ModalTrigger } from '@synoptic/ui-kit/modal/modal.tsx';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router';
import { CustomCollection, StreamDeck } from '@/__generated__/schema-types.ts';
import { DeleteStreamWrapper } from '@/components/streams/delete-stream/delete-stream-wrapper.tsx';
import { bannerActionsButton } from '@/components/streams/stream-banner/banner-actions-menu.css.ts';
import { CancelStreamSubscriptionModalContent } from '@/components/streams/toggle-paid-stream-subscription/cancel-stream-subscription-modal-content.tsx';
import { TickerPickerModalContent } from '@/components/view-price-chart-link/view-price-chart-link.tsx';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { AddUsersToStreamModal } from '@/routes/_app.p.streams_.$id/stream-banner/add-users-to-stream-modal.tsx';
import { ExportStreamPostsModal } from '@/routes/_app.p.streams_.$id/stream-banner/export-stream-posts-modal.tsx';
import { HoldStreamModal } from '@/routes/_app.p.streams_.$id/stream-banner/hold-stream-modal.tsx';
import { StreamAdminActions } from '@/routes/_app.p.streams_.$id/stream-banner/stream-admin-actions.tsx';
import {
  setStreamUsersType,
  StreamUserType,
} from '@/routes/_app.p.streams_.$id_.users/stream-users-type.ts';
import { useAuthInfo } from '@/routes/_app/use-auth-info';
import { AddToStreamDeckDropdownMenuItem } from './add-to-stream-deck-dropdown-menu-item';

export interface ActionsMenuProps {
  streamId: string;
  isAuthor: boolean;
  isPaidStream: boolean;
  isPrivateStream: boolean;
  isDraft: boolean;
  isCancelled: boolean;
  accessAllowed: boolean;
  canAddToStreamDeck: boolean;
  streamDecks: StreamDeck[];
  onStreamColumnSelect: (
    streamDeck: StreamDeck,
    streamDeckColumn: CustomCollection | 'new',
  ) => void;
  isCustomStream: boolean;
}

export const BannerActionsMenu: FC<ActionsMenuProps> = ({
  streamId,
  isAuthor,
  isPaidStream,
  isCancelled,
  accessAllowed,
  canAddToStreamDeck,
  streamDecks,
  onStreamColumnSelect,
  isCustomStream,
  isPrivateStream,
  isDraft,
}) => {
  const isTablet = useIsTablet();
  const { isAdmin } = useAuthInfo();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancellation, setIsCancellation] = useState(false);
  const [isDeletion, setIsDeletion] = useState(false);
  const [isHold, setIsHold] = useState(false);
  const [addUsersModalOpen, setAddUsersModalOpen] = useState(false);
  const [isExport, setIsExport] = useState(false);
  const [isPriceChart, setIsPriceChart] = useState(false);

  const canCancelSubscription = isPaidStream && !isCancelled && accessAllowed;

  const actions = [];

  if (isAdmin) {
    actions.push(
      <StreamAdminActions
        key="admin-actions"
        streamId={streamId}
        openHoldModal={() => setIsHold(true)}
        openAddUsersModal={() => setAddUsersModalOpen(true)}
        openExportModal={() => setIsExport(true)}
        openPriceChartModal={() => setIsPriceChart(true)}
      />,
    );
  }

  if (canAddToStreamDeck && isTablet) {
    actions.push(
      <AddToStreamDeckDropdownMenuItem
        key="add-to-stream-deck"
        streamDecks={streamDecks}
        onStreamColumnSelect={onStreamColumnSelect}
      />,
    );
  }

  if (isCustomStream) {
    actions.push(
      <DropdownMenuItem key="targets-link" asChild>
        <Link to={paths.makeStreamTargets(streamId)}>View targets</Link>
      </DropdownMenuItem>,
    );
  }

  if (isPrivateStream && isAuthor && !isDraft) {
    actions.push(
      <DropdownMenuItem
        key="stream-invitees-link"
        onSelect={() => {
          setStreamUsersType(streamId, StreamUserType.Invites);
          navigate(paths.makeStreamUsers(streamId));
        }}
      >
        View pending invites
      </DropdownMenuItem>,
    );
  }

  actions.push(
    <DropdownMenuItem key="api-settings-link" asChild>
      <Link to={paths.settingsApiKeys}>See API key & settings</Link>
    </DropdownMenuItem>,
  );

  if (isAuthor) {
    const deleteAction = (
      <ModalTrigger key="delete" asChild>
        <DropdownMenuItem
          onSelect={() => setIsDeletion(true)}
          key="delete-stream"
          variant={'critical'}
        >
          Delete stream
        </DropdownMenuItem>
      </ModalTrigger>
    );

    actions.push(deleteAction);
  }

  if (canCancelSubscription && !isAuthor) {
    const cancelAction = (
      <ModalTrigger key="cancel" asChild>
        <DropdownMenuItem
          onSelect={() => setIsCancellation(true)}
          key="cancel-subscription"
          variant={'critical'}
        >
          Cancel subscription
        </DropdownMenuItem>
      </ModalTrigger>
    );

    actions.push(cancelAction);
  }

  return (
    <ModalRoot
      open={isModalOpen}
      onOpenChange={(open) => {
        setIsModalOpen(open);
        setIsDeletion(false);
        setIsCancellation(false);
        setIsHold(false);
        setAddUsersModalOpen(false);
        setIsExport(false);
        setIsPriceChart(false);
      }}
    >
      <DropdownMenuRoot>
        <DropdownMenuTrigger>
          <IconButton
            size={isTablet ? 'medium' : 'small'}
            variant="ghost"
            aria-label="Stream actions menu"
            className={bannerActionsButton}
          >
            <MoreHorizIcon style={{ height: 20, width: 20 }} />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={'end'}>{actions}</DropdownMenuContent>
      </DropdownMenuRoot>

      {isCancellation && (
        <CancelStreamSubscriptionModalContent
          streamId={streamId}
          onOpenChange={setIsModalOpen}
        />
      )}

      {isDeletion && (
        <DeleteStreamWrapper
          streamId={streamId}
          onCancel={() => setIsModalOpen(false)}
          isPaidStream={isPaidStream}
        />
      )}

      {isHold && (
        <HoldStreamModal streamId={streamId} onOpenChange={setIsModalOpen} />
      )}

      {addUsersModalOpen && (
        <AddUsersToStreamModal
          streamId={streamId}
          onOpenChange={setIsModalOpen}
        />
      )}

      {isExport && (
        <ExportStreamPostsModal
          streamId={streamId}
          onOpenChange={setIsModalOpen}
        />
      )}

      {isPriceChart && <TickerPickerModalContent streamId={streamId} />}
    </ModalRoot>
  );
};
